import { useCallback, useEffect, useState } from 'react';

import { useRereadWhenVisible } from '../reread-when-visible';
import { applyReadReceipt, type SafetyBoardPage, type SafetyBoardReadReceipt } from './model';
import { createSafetyBoardRepository, type SafetyBoardRepository } from './repository';
import { useSafetyBoardSite } from './site-context';

export type SafetyBoardSnapshot =
  | Readonly<{ state: 'loading' }>
  | Readonly<{ state: 'unavailable' }>
  | Readonly<{ state: 'ready'; page: SafetyBoardPage }>;

/** One highlighted item and five compact rows; the archive holds the longer history. */
export const HOME_BOARD_ROWS = 6;

/**
 * The home board's own read, started before the home exists.
 *
 * Nothing is held from an earlier session: this is one request made in this document whose
 * answer the card is still waiting for when it mounts. It carries only a row count -- the same
 * count the card asks for -- so there is no guessed input that would have to be re-issued.
 */
let prefetchedPage: Readonly<{
  siteId: string;
  limit: number;
  page: Promise<SafetyBoardPage | null>;
}> | null = null;

export function prefetchSafetyBoardPage(limit: number, siteId?: string): void {
  if (!siteId) return;
  prefetchedPage ??= {
    siteId,
    limit,
    page: createSafetyBoardRepository(siteId)
      .then((repository) => repository.list(limit))
      .catch(() => null),
  };
}

/**
 * Hands the warm-started page to its first caller and forgets it. A screen asking for a
 * different number of rows gets nothing: a ten-row page is not the answer to a fifty-row
 * question.
 */
export function takeSafetyBoardPagePrefetch(limit: number, siteId?: string | null): Promise<SafetyBoardPage | null> | null {
  if (!prefetchedPage || prefetchedPage.limit !== limit || prefetchedPage.siteId !== siteId) return null;
  const started = prefetchedPage.page;
  prefetchedPage = null;
  return started;
}

export function discardSafetyBoardPagePrefetch(): void {
  prefetchedPage = null;
}

/**
 * One list call per screen open.
 *
 * The page it returns carries everything a row and the detail dialog render, so nothing else on
 * these screens reads from the server: the dialog is opened from the row already in hand, and
 * the only other call a screen can make is a write (a read receipt or a publisher command),
 * each of which refreshes from its own server response.
 */
export function useSafetyBoardPage(limit: number) {
  const siteId = useSafetyBoardSite();
  const [snapshot, setSnapshot] = useState<SafetyBoardSnapshot>({ state: 'loading' });
  const [reading, setReading] = useState(0);
  const reread = useCallback(() => setReading((value) => value + 1), []);
  useRereadWhenVisible(reread);

  const read = useCallback(
    async (repository: SafetyBoardRepository) => {
      const page = await repository.list(limit);
      setSnapshot(page ? { state: 'ready', page } : { state: 'unavailable' });
    },
    [limit],
  );

  useEffect(() => {
    let alive = true;
    /* Only the first read of the document may accept the warm start. Every later one -- the tab
     * being looked at again -- is a fresh call, because the point of it is that nothing on
     * screen may be older than this moment. */
    const inFlight =
      (reading === 0 ? takeSafetyBoardPagePrefetch(limit, siteId) : null) ??
      createSafetyBoardRepository(siteId).then((repository) => repository.list(limit));
    void inFlight
      .then((page) => {
        if (alive) setSnapshot(page ? { state: 'ready', page } : { state: 'unavailable' });
      })
      .catch(() => {
        if (alive) setSnapshot({ state: 'unavailable' });
      });
    return () => {
      alive = false;
    };
  }, [limit, reading, siteId]);

  const reload = useCallback(async () => {
    const repository = await createSafetyBoardRepository(siteId);
    await read(repository);
  }, [read, siteId]);

  /** Applied only from a server receipt, never ahead of one. */
  const acceptReadReceipt = useCallback((receipt: SafetyBoardReadReceipt) => {
    setSnapshot((current) =>
      current.state === 'ready'
        ? { state: 'ready', page: applyReadReceipt(current.page, receipt) }
        : current,
    );
  }, []);

  return { snapshot, reload, acceptReadReceipt };
}
